import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Select } from 'antd';
import React, { useEffect, useState } from 'react'
import { Button, Col, Form, Row } from 'react-bootstrap';
import { accessLevelOptions, setObjectType } from '../../utils';
import { DatabaseDatasource, Datasource, FileSystemDatasource, MessageBrokerDatasource, ObjectStorageDatasource } from '../../models/Datasource';

const datasourceTypes = [
  { label: 'Message Broker', value: 'messageBroker', icon: solid('comments') },
  { label: 'Object Storage', value: 'objectStorage', icon: solid('memory') },
  { label: 'Tabular Storage', value: 'database', icon: solid('database') },
  { label: 'FileSystem Storage', value: 'fileSystem', icon: solid('folder-tree') },
];

type Props = {
  datasource: Datasource;
  onSubmit?: (datasource: Datasource) => void;
  onCancel?: React.MouseEventHandler<HTMLButtonElement>;
}

export default function DatasourceForm({ datasource, onSubmit, onCancel }: Props) {
  const [editingDatasource, setEditingDatasource] = useState(datasource);
  const [validated, setValidated] = useState(false);

  useEffect(() => {
    setEditingDatasource(datasource);
  }, [datasource]);

  const handleFieldChange = (field: string, checkbox?: boolean) => (evt: any) => {
    const value = (field === "type" || field === "accessLevel") ? evt : checkbox ? evt.target.checked : evt.target.value;

    setEditingDatasource({
      ...editingDatasource,
      [field]: value,
    });
  }

  const handleSubmit = (evt: React.FormEvent<HTMLFormElement>) => {
    const form = evt.currentTarget;

    evt.stopPropagation();
    evt.preventDefault();

    setValidated(true);

    if (form.checkValidity() === true && datasource.type) {
      onSubmit?.(editingDatasource);
    }
  }

  const renderMessageBrokerFields = () => {
    return setObjectType<MessageBrokerDatasource>(editingDatasource) && (
      <>
        <Form.Group controlId="datasource-brokers" className="mb-2">
          <Form.Label>Brokers</Form.Label>
          <Form.Control placeholder="Data Source Brokers" size="sm" value={editingDatasource.brokers} onChange={handleFieldChange("brokers")} required />
          <Form.Control.Feedback type="invalid">Data Source Brokers is required.</Form.Control.Feedback>
        </Form.Group>

        <Row className="gx-0">
          <Col md={6} className="pe-2 mb-2">
            <Form.Group controlId="datasource-username">
              <Form.Label>Username</Form.Label>
              <Form.Control placeholder="Data Source Username" size="sm" value={editingDatasource.username || ""} onChange={handleFieldChange('username')} />
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group controlId="datasource-password">
              <Form.Label>Password</Form.Label>
              <Form.Control placeholder="Data Source Password" size="sm" value={editingDatasource.password || ""} onChange={handleFieldChange('password')} />
            </Form.Group>
          </Col>
        </Row>

        <Row className="gx-0">
          <Col md={6} className="pe-2 mb-2">
            <Form.Group controlId="datasource-cafile">
              <Form.Label>CA File</Form.Label>
              <Form.Control as="textarea" placeholder="Data Source CA File" size="sm" value={editingDatasource.cafile || ""} onChange={handleFieldChange('cafile')} />
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group controlId="datasource-cert-file">
              <Form.Label>Cert File</Form.Label>
              <Form.Control as="textarea" placeholder="Data Source Cert File" size="sm" value={editingDatasource.certfile || ""} onChange={handleFieldChange('certfile')} />
            </Form.Group>
          </Col>

          <Col md={6} className="pe-2 mb-2">
            <Form.Group controlId="datasource-keyfile">
              <Form.Label>Key File</Form.Label>
              <Form.Control as="textarea" placeholder="Data Source Key File" size="sm" value={editingDatasource.keyfile || ""} onChange={handleFieldChange('keyfile')} />
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group controlId="datasource-key-server-config">
              <Form.Label>Key Server Config</Form.Label>
              <Form.Control as="textarea" placeholder="Data Source Key Server Config" size="sm" value={editingDatasource.keyServerConfig || ""} onChange={handleFieldChange('keyServerConfig')} />
            </Form.Group>
          </Col>
        </Row>
      </>
    )
  }

  const renderObjectStorageFields = () => {
    return setObjectType<ObjectStorageDatasource>(editingDatasource) && (
      <>
        <Row className="gx-0 mb-2">
          <Col className="pe-2 mb-2">
            <Form.Group controlId="datasource-host">
              <Form.Label>Host</Form.Label>
              <Form.Control placeholder="Data Source Host" size="sm" value={editingDatasource.host} onChange={handleFieldChange('host')} required />
            </Form.Group>
          </Col>

          <Col xs={4}>
            <Form.Group controlId="datasource-port">
              <Form.Label>Port</Form.Label>
              <Form.Control placeholder="Port" size="sm" value={editingDatasource.port || ""} onChange={handleFieldChange('port')} type="number" />
            </Form.Group>
          </Col>
        </Row>

        <Row className="align-items-end mb-2 gx-2">
          <Col md={6}>
            <Form.Group controlId="datasource-minio-bucket">
              <Form.Label>Bucket</Form.Label>
              <Form.Control placeholder="Data Source Bucket" size="sm" value={editingDatasource.bucket || ""} onChange={handleFieldChange('bucket')} required />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Check label="Secure" type="checkbox" id="datasource-secure" checked={editingDatasource.secure} onChange={handleFieldChange('secure', true)} />
          </Col>
        </Row>

        <Row className="gx-2 mb-2">
          <Col md={6}>
            <Form.Group controlId="datasource-minio-accessKey">
              <Form.Label>Access Key</Form.Label>
              <Form.Control placeholder="Data Source Access Key" size="sm" value={editingDatasource.accessKey || ""} onChange={handleFieldChange('accessKey')} required />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group controlId="datasource-minio-secretKey">
              <Form.Label>Secret Key</Form.Label>
              <Form.Control placeholder="Data Source Secret Key" size="sm" value={editingDatasource.secretKey || ""} onChange={handleFieldChange('secretKey')} required />
            </Form.Group>
          </Col>
        </Row>

        <Row className="gx-2">
          <Col md={6}>
            <Form.Group controlId="datasource-minio-prefixPath" className="mb-2">
              <Form.Label>Prefix Path</Form.Label>
              <Form.Control placeholder="Data Source Prefix Path" size="sm" value={editingDatasource.prefixPath || ""} onChange={handleFieldChange('prefixPath')} />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group controlId="datasource-minio-regionName">
              <Form.Label>Region Name</Form.Label>
              <Form.Control placeholder="Data Source Region Name" size="sm" value={editingDatasource.regionName || ""} onChange={handleFieldChange('regionName')} />
            </Form.Group>
          </Col>
        </Row>
      </>
    )
  }

  const renderDatabaseFields = () => {
    return setObjectType<DatabaseDatasource>(editingDatasource) && (
      <>
        <Row className="gx-0">
          <Col className="mb-2 pe-2" md={4}>
            <Form.Group controlId="datasource-host">
              <Form.Label>Host</Form.Label>
              <Form.Control placeholder="Data Source Host" size="sm" value={editingDatasource.host} onChange={handleFieldChange('host')} required />
            </Form.Group>
          </Col>

          <Col className="mb-2 pe-2" md={4}>
            <Form.Group controlId="datasource-port">
              <Form.Label>Port</Form.Label>
              <Form.Control placeholder="Port" size="sm" value={editingDatasource.port} onChange={handleFieldChange('port')} type="number" required />
            </Form.Group>
          </Col>

          <Col md={4}>
            <Form.Group className="mb-2" controlId="datasource-database">
              <Form.Label>Database</Form.Label>
              <Form.Control placeholder="Data Source Database" size="sm" value={editingDatasource.database || ""} onChange={handleFieldChange('database')} required />
            </Form.Group>
          </Col>
        </Row>

        <Row className="gx-0">
          <Col md={6} className="pe-2 mb-2">
            <Form.Group controlId="datasource-user">
              <Form.Label>User</Form.Label>
              <Form.Control placeholder="Data Source User" size="sm" value={editingDatasource.user || ""} onChange={handleFieldChange('user')} required />
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group controlId="datasource-password">
              <Form.Label>Password</Form.Label>
              <Form.Control placeholder="Data Source Password" size="sm" value={editingDatasource.password || ""} onChange={handleFieldChange('password')} required />
            </Form.Group>
          </Col>
        </Row>

        <Form.Group className="mb-2" controlId="datasource-catalogue">
          <Form.Label>Catalogue</Form.Label>
          <Form.Control size="sm" placeholder="Catalogue" value={editingDatasource.catalogue || ""} onChange={handleFieldChange('catalogue')} />
        </Form.Group>

        <Row className="gx-0 mb-2">
          <Col className="pe-2">
            <Form.Group className="mb-2" controlId="datasource-thrift-host">
              <Form.Label>Thrift Host</Form.Label>
              <Form.Control size="sm" placeholder="Thrift Host" value={editingDatasource.thriftHost || ""} onChange={handleFieldChange('thriftHost')} />
            </Form.Group>
          </Col>

          <Col xs={3}>
            <Form.Group className="mb-2" controlId="datasource-thrift-port">
              <Form.Label>Thrift Port</Form.Label>
              <Form.Control size="sm" type="number" placeholder="Thrift Port" value={editingDatasource.thriftPort || ""} onChange={handleFieldChange('thriftPort')} />
            </Form.Group>
          </Col>
        </Row>
      </>
    )
  }

  const renderFileSystemFields = () => {
    return setObjectType<FileSystemDatasource>(editingDatasource) && (
      <Row className="gx-0">
        <Col md={4} className="pe-2 mb-2">
          <Form.Group controlId="datasource-file-path">
            <Form.Label>File Path</Form.Label>
            <Form.Control placeholder="Data Source File Path" size="sm" value={editingDatasource.path || ""} onChange={handleFieldChange('path')} required />
          </Form.Group>
        </Col>

        <Col md={4} className="pe-2 mb-2">
          <Form.Group controlId="datasource-file-name" className="mb-2">
            <Form.Label>File Name</Form.Label>
            <Form.Control placeholder="Data Source File Name" size="sm" value={editingDatasource.filename || ""} onChange={handleFieldChange('filename')} required />
          </Form.Group>
        </Col>

        <Col md={4}>
          <Form.Group controlId="datasource-file-extension">
            <Form.Label>File Extension</Form.Label>
            <Form.Control placeholder="Data Source File Extension" size="sm" value={editingDatasource.extension || ""} onChange={handleFieldChange('extension')} required />
          </Form.Group>
        </Col>
      </Row>
    )
  }

  return (
    <Form noValidate validated={validated} onSubmit={handleSubmit}>
      <Row className="gx-0 align-items-center">
        <Col md={6} className="pe-2">
          <Form.Group className="mb-2" controlId="datasource-name">
            <Form.Label>Name</Form.Label>
            <Form.Control size="sm" placeholder="Data Source name" value={editingDatasource.name} onChange={handleFieldChange('name')} required />
            <Form.Control.Feedback type="invalid">Data Source Name is required.</Form.Control.Feedback>
          </Form.Group>

          <Form.Group controlId="datasource-access-level" className="mb-2">
            <Form.Label>Access Level</Form.Label>
            <Select
              placeholder="Select data source access level"
              value={editingDatasource.accessLevel}
              allowClear
              onChange={handleFieldChange('accessLevel')}
              className="w-100"
              status={validated && !editingDatasource.accessLevel ? 'error' : ''}
            >
              {accessLevelOptions
                .filter(option => window.__env.ALLOWED_ACCESS_LEVELS ? window.__env.ALLOWED_ACCESS_LEVELS.includes(option.value) : true)
                .map((option, index) => (
                  <Select.Option key={index} value={option.value} label={option.label}>
                    <div>
                      <FontAwesomeIcon icon={option.icon} fixedWidth />
                      <span className="ms-2">{option.label}</span>
                    </div>
                  </Select.Option>
                ))}
            </Select>
            {validated && !editingDatasource.accessLevel && <Form.Control.Feedback className="d-block" type="invalid">Data source access level is required.</Form.Control.Feedback>}
          </Form.Group>
        </Col>

        <Col md={6}>
          <Form.Group className="mb-2" controlId="datasource-description">
            <Form.Label>Description</Form.Label>
            <Form.Control size="sm" placeholder="Data Source description" as="textarea" rows={4} value={editingDatasource.description} onChange={handleFieldChange('description')} />
          </Form.Group>
        </Col>
      </Row>

      <Row className="gx-0 align-items-center">
        <Col className="pe-2 mb-2">
          <Form.Group controlId="datasource-type">
            <Form.Label>Type</Form.Label>
            <Select
              className="w-100"
              placeholder="Data Source Type"
              onChange={handleFieldChange("type")}
              value={editingDatasource.type}
            >
              {datasourceTypes.map((option, index) => (
                <Select.Option key={index} value={option.value}>
                  <FontAwesomeIcon icon={option.icon} fixedWidth />
                  <span className="ms-2">{option.label}</span>
                </Select.Option>
              ))}
            </Select>
          </Form.Group>
        </Col>

        <Col md={4} className="mb-2">
          <Form.Group controlId="datasource-driver">
            <Form.Label>Driver</Form.Label>
            <Form.Control size="sm" placeholder="Data Source Driver" value={editingDatasource.driverType} onChange={handleFieldChange('driverType')} required />
            <Form.Control.Feedback type="invalid">Data Source Driver is required.</Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>

      {editingDatasource.type === 'messageBroker' && renderMessageBrokerFields()}
      {editingDatasource.type === 'objectStorage' && renderObjectStorageFields()}
      {editingDatasource.type === 'database' && renderDatabaseFields()}
      {editingDatasource.type === 'fileSystem' && renderFileSystemFields()}

      <div className="d-flex justify-content-between mt-3">
        <Button variant="light" size="sm" type="button" className="border px-3" onClick={onCancel}>
          <FontAwesomeIcon icon={solid('chevron-left')} />
          <span className="ms-2">Cancel</span>
        </Button>
        <Button variant="success" size="sm" type="submit">
          <FontAwesomeIcon icon={solid('check')} />
          <span className="ms-2">Confirm</span>
        </Button>
      </div>
    </Form>
  )
}
